// 📄 pages/SAP-BTP/components/GuardarEnPerfilModal.tsx
// "Guardar estos valores en un perfil" (docs/plans/plan-perfiles-valores.md,
// P3): los cambios hechos a mano en un iFlow se guardan como valores de un
// perfil (nuevo o existente), con "Reemplaza a" = el valor original. Así la
// próxima vez basta con "Aplicar perfil". Los que ya están (mismo valor y
// mismo "Reemplaza a") no se repiten.
import React, { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, X } from 'lucide-react';
import { ResumenPerfil, TipoValorPerfil, ValorPerfil, crearPerfil, guardarPerfil, listarPerfiles } from '../../../services/perfilValoresService.ts';
import { cargarPerfil, olvidarPerfilesEnMemoria, recordarPerfilUsado } from './SelectorPerfil.tsx';

interface GuardarEnPerfilModalProps {
  orgId: string;
  cambios: { etiqueta: string; tipo: TipoValorPerfil; valor: string; reemplaza: string }[];
  onListo: (mensaje: string) => void;
  onCerrar: () => void;
}

export default function GuardarEnPerfilModal({ orgId, cambios, onListo, onCerrar }: GuardarEnPerfilModalProps) {
  const [perfiles, setPerfiles] = useState<ResumenPerfil[] | null>(null);
  const [destino, setDestino] = useState('nuevo');
  const [nombre, setNombre] = useState('');
  const [ambiente, setAmbiente] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listarPerfiles(orgId).then(setPerfiles).catch(() => setPerfiles([]));
  }, [orgId]);

  const guardar = async () => {
    setError(null);
    const nuevos: ValorPerfil[] = cambios.map((c) => ({ nombre: c.etiqueta.slice(0, 120), valor: c.valor, tipo: c.tipo, reemplaza: c.reemplaza }));
    if (destino === 'nuevo' && !nombre.trim()) { setError('Ponle un nombre al perfil (ej. "ACME – QAS").'); return; }
    setOcupado(true);
    try {
      if (destino === 'nuevo') {
        const r = await crearPerfil(orgId, { nombre: nombre.trim(), ambiente: ambiente.trim(), valores: nuevos });
        recordarPerfilUsado(orgId, r.id);
        onListo(`Perfil "${nombre.trim()}" creado con ${nuevos.length} valor${nuevos.length === 1 ? '' : 'es'}.`);
      } else {
        const p = await cargarPerfil(orgId, destino);
        const ya = new Set(p.valores.map((v) => `${v.reemplaza}\u0000${v.valor}`));
        const agregar = nuevos.filter((v) => !ya.has(`${v.reemplaza}\u0000${v.valor}`));
        if (!agregar.length) { onListo(`"${p.nombre}" ya tenía estos valores.`); return; }
        await guardarPerfil(orgId, p.id, { valores: [...p.valores, ...agregar] }, p.revision);
        recordarPerfilUsado(orgId, p.id);
        onListo(`Se agregaron ${agregar.length} valor${agregar.length === 1 ? '' : 'es'} a "${p.nombre}".`);
      }
      olvidarPerfilesEnMemoria();
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar en el perfil.');
    } finally {
      setOcupado(false);
    }
  };

  const inputCls = 'w-full bg-zinc-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50';

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onCerrar}>
      <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
          <div>
            <h3 className="text-sm font-semibold text-white flex items-center gap-2"><KeyRound className="w-4 h-4 text-cyan-400" /> Guardar estos valores en un perfil</h3>
            <p className="text-[11px] text-gray-400 mt-1">Cada cambio queda como un valor del perfil, con "Reemplaza a" = el valor original. La próxima vez, "Aplicar perfil" los pone solo.</p>
          </div>
          <button onClick={onCerrar} className="text-gray-400 hover:text-white" aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select className={inputCls} value={destino} onChange={(e) => setDestino(e.target.value)} aria-label="Perfil de destino" disabled={perfiles === null}>
              <option value="nuevo">Perfil nuevo…</option>
              {(perfiles || []).map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.ambiente ? ` (${p.ambiente})` : ''}</option>)}
            </select>
            {destino === 'nuevo' && (
              <>
                <input className={inputCls} value={nombre} maxLength={120} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre (ej. ACME – QAS)" autoFocus />
                <input className={inputCls} value={ambiente} maxLength={40} onChange={(e) => setAmbiente(e.target.value)} placeholder="Ambiente (opcional)" />
              </>
            )}
          </div>
          <ul className="space-y-1.5">
            {cambios.map((c, i) => (
              <li key={i} className="rounded-lg border border-white/10 bg-white/5 p-2 text-[11px]">
                <p className="text-white">{c.etiqueta}</p>
                <p className="font-mono text-cyan-300 break-all">{c.valor || '(vacío)'}</p>
                <p className="font-mono text-gray-500 break-all">reemplaza a: {c.reemplaza || '(vacío)'}</p>
              </li>
            ))}
          </ul>
        </div>

        <footer className="flex justify-end gap-2 p-4 border-t border-white/10">
          <button onClick={onCerrar} className="px-4 py-2 text-sm text-gray-300 hover:text-white">Cancelar</button>
          <button onClick={guardar} disabled={ocupado || !cambios.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
            {ocupado && <RefreshCw className="w-4 h-4 animate-spin" />} Guardar {cambios.length} valor{cambios.length === 1 ? '' : 'es'}
          </button>
        </footer>
      </div>
    </div>
  );
}
